
import React, { useState, useEffect } from 'react';
import Button from '../../components/base/Button';
import FileUpload from '../../components/feature/FileUpload';
import DocumentList from '../../components/feature/DocumentList';
import FolderManager from '../../components/feature/FolderManager';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

interface User {
  id: string;
  email: string;
  full_name: string;
  access_level: 'master' | 'editor' | 'partner' | 'outsider';
  require_password_change?: boolean;
}

interface PortalUser {
  id: string;
  email: string;
  full_name: string;
  access_level: string;
  is_active: boolean;
  created_at: string;
}

interface Folder {
  id: string;
  name: string;
  created_at: string;
}

const InvestorPortal: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [authSession, setAuthSession] = useState<any>(null);

  // Password change states
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Password reset states
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  // Login form state
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });

  // Master-only states
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PortalUser | null>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [newUser, setNewUser] = useState({
    email: '',
    password_hash: '',
    full_name: '',
    access_level: 'outsider'
  });

  // New user creation options
  const [useSupabaseAuth, setUseSupabaseAuth] = useState(true);
  const [requirePasswordChange, setRequirePasswordChange] = useState(true);
  const [generatedPassword, setGeneratedPassword] = useState('');

  // New state for folder selection and tab handling
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('folders');

  // New state for user permissions management
  const [showUserPermissionsManager, setShowUserPermissionsManager] = useState(false);

  useEffect(() => {
    const savedUser = localStorage.getItem('portalUser');
    const savedSession = localStorage.getItem('portalSession');
    if (savedUser) {
      const userData = JSON.parse(savedUser);
      setUser(userData);
      setIsLoggedIn(true);
      if (savedSession) {
        setAuthSession(JSON.parse(savedSession));
      }
      
      // Check if user needs to change password
      if (userData.require_password_change) {
        setShowPasswordChange(true);
      }
      
      if (userData.access_level === 'master') {
        loadUsers();
        loadFolders();
      }
    }

    // Check for password reset in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('reset') === 'true') {
      setShowPasswordReset(true);
    }
  }, []);

  // Add keyboard event listener for escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showPasswordChange) {
          setShowPasswordChange(false);
          setNewPassword('');
          setConfirmPassword('');
          setPasswordChangeError('');
          // Don't modify the user state - keep the password change requirement
        }
        if (showPasswordReset) {
          setShowPasswordReset(false);
          setResetEmail('');
          setResetMessage('');
        }
        if (showPermissions) {
          setShowPermissions(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showPasswordChange, showPasswordReset, showPermissions, user]);

  // Session refresh utility
  const refreshSession = async () => {
    const storedSession = localStorage.getItem('investorSession');
    if (!storedSession) return false;

    try {
      const session = JSON.parse(storedSession);
      const response = await fetch('https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/auth-management', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'refreshSession',
          authToken: session.refresh_token || session.access_token
        })
      });

      const data = await response.json();
      
      if (data.session) {
        localStorage.setItem('investorSession', JSON.stringify(data.session));
        setUser(data.user);
        return true;
      } else if (data.expired) {
        // Session expired, force logout
        localStorage.removeItem('investorSession');
        setUser(null);
        setIsLoggedIn(false);
        return false;
      }
    } catch (error) {
      console.error('Session refresh failed:', error);
      localStorage.removeItem('investorSession');
      setUser(null);
      setIsLoggedIn(false);
      return false;
    }
    
    return false;
  };

  // Check session validity on mount and periodically
  useEffect(() => {
    const checkSession = async () => {
      const storedSession = localStorage.getItem('investorSession');
      if (storedSession && !user) {
        try {
          const session = JSON.parse(storedSession);
          
          // Check if session is expired (if it has an expires_at field)
          if (session.expires_at) {
            const expiresAt = new Date(session.expires_at * 1000);
            const now = new Date();
            const timeUntilExpiry = expiresAt.getTime() - now.getTime();
            
            // If session expires in less than 5 minutes, try to refresh
            if (timeUntilExpiry < 5 * 60 * 1000) {
              const refreshed = await refreshSession();
              if (!refreshed) {
                return; // Session refresh failed, user will be logged out
              }
            }
          }
          
          // Set user from stored session
          setUser(session.user || JSON.parse(localStorage.getItem('investorUser') || '{}'));
          setIsLoggedIn(true);
          
          // Check if password change is required
          const requirePasswordChange = localStorage.getItem('requirePasswordChange') === 'true';
          setShowPasswordChange(requirePasswordChange);
          
        } catch (error) {
          console.error('Invalid session data:', error);
          localStorage.removeItem('investorSession');
          localStorage.removeItem('investorUser');
          localStorage.removeItem('requirePasswordChange');
        }
      }
    };

    checkSession();

    // Set up periodic session check (every 10 minutes)
    const sessionCheckInterval = setInterval(async () => {
      if (user && isLoggedIn) {
        await refreshSession();
      }
    }, 10 * 60 * 1000);

    return () => clearInterval(sessionCheckInterval);
  }, [user, isLoggedIn]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');

    try {
      // Try Supabase Auth first
      const authResponse = await fetch('https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/auth-management', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: loginForm.email,
          password: loginForm.password,
          action: 'login_with_auth'
        }),
      });

      if (authResponse.ok) {
        const authData = await authResponse.json();
        setUser(authData.user);
        setIsLoggedIn(true);
        setAuthSession(authData.session);
        localStorage.setItem('portalUser', JSON.stringify(authData.user));
        localStorage.setItem('portalSession', JSON.stringify(authData.session));
        
        // Check if user needs to change password
        if (authData.user.require_password_change) {
          setShowPasswordChange(true);
        }
        
        if (authData.user.access_level === 'master') {
          loadUsers();
          loadFolders();
        }
        return;
      }

      // Fallback to legacy auth
      const response = await fetch('https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/user-auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: loginForm.email,
          password: loginForm.password,
          action: 'login'
        }),
      });

      const data = await response.json();

      if (response.ok && data.user) {
        setUser(data.user);
        setIsLoggedIn(true);
        localStorage.setItem('portalUser', JSON.stringify(data.user));
        
        if (data.user.access_level === 'master') {
          loadUsers();
          loadFolders();
        }
      } else {
        setLoginError(data.error || 'Login failed');
      }
    } catch (error) {
      setLoginError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');

    try {
      // Get current session
      const storedSession = localStorage.getItem('investorSession');
      const storedUser = localStorage.getItem('investorUser');
      
      let authToken = null;
      let userEmail = null;

      if (storedSession) {
        const session = JSON.parse(storedSession);
        authToken = session.access_token;
      }

      if (storedUser) {
        const userData = JSON.parse(storedUser);
        userEmail = userData.email;
      } else if (user) {
        userEmail = user.email;
      }

      if (!authToken && !userEmail) {
        throw new Error('Authentication session missing');
      }

      const response = await fetch('https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/auth-management', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'changePassword',
          email: userEmail,
          newPassword,
          authToken
        })
      });

      const data = await response.json();

      if (data.error) {
        if (data.error.includes('session') || data.error.includes('token')) {
          // Try to refresh session first
          const refreshed = await refreshSession();
          if (refreshed) {
            // Retry password change with refreshed session
            return handlePasswordChange(e);
          } else {
            // Session refresh failed, logout user
            handleLogout();
            return;
          }
        }
        throw new Error(data.error);
      }

      // Password changed successfully
      setShowPasswordChange(false);
      localStorage.removeItem('requirePasswordChange');
      setNewPassword('');
      setConfirmPassword('');
      
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordError(error instanceof Error ? error.message : 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetMessage('');

    try {
      const response = await fetch('https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/auth-management', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'reset_password',
          resetEmail: resetEmail
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setResetMessage('Password reset email sent! Check your inbox.');
        setResetEmail('');
      } else {
        setResetMessage(data.error || 'Failed to send reset email');
      }
    } catch (error) {
      setResetMessage('Error sending reset email');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUser(null);
    setAuthSession(null);
    setLoginForm({ email: '', password: '' });
    setShowUserManagement(false);
    setShowPermissions(false);
    setShowPasswordChange(false);
    localStorage.removeItem('portalUser');
    localStorage.removeItem('portalSession');
    localStorage.removeItem('investorSession');
    localStorage.removeItem('investorUser');
    localStorage.removeItem('requirePasswordChange');
  };

  const createUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || user.access_level !== 'master') return;

    try {
      if (useSupabaseAuth) {
        // Generate a secure temporary password for Supabase Auth
        const tempPassword = Math.random().toString(36).slice(-8) + Math.random().toString(36).slice(-8).toUpperCase() + '!';

        // Use new Supabase Auth method
        const response = await fetch('https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/auth-management', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: user.email,
            action: 'create_user_with_auth',
            newUser: {
              ...newUser,
              password_hash: tempPassword // Provide the generated password
            },
            requirePasswordChange
          }),
        });

        const data = await response.json();
        
        if (response.ok) {
          const actualTempPassword = data.tempPassword || tempPassword;
          setGeneratedPassword(actualTempPassword);
          
          // Reset form
          setNewUser({
            email: '',
            password_hash: '',
            full_name: '',
            access_level: 'outsider'
          });
          loadUsers();

          // Show email option
          const emailSubject = encodeURIComponent('Your Investor Portal Account');
          const emailBody = encodeURIComponent(
            `Hello ${newUser.full_name},\n\n` +
            `Your investor portal account has been created.\n\n` +
            `Login Details:\n` +
            `Email: ${newUser.email}\n` +
            `Temporary Password: ${actualTempPassword}\n\n` +
            `Please log in at: ${window.location.origin}/investor-portal\n\n` +
            (requirePasswordChange ? 'You will be prompted to change your password on first login.\n\n' : '') +
            `Best regards,\nCanal Realty Team`
          );

          const mailtoLink = `mailto:${newUser.email}?subject=${emailSubject}&body=${emailBody}`;

          if (confirm(`User created successfully!\n\nTemporary Password: ${actualTempPassword}\n\nWould you like to send login details via email?`)) {
            window.location.href = mailtoLink;
          }
        } else {
          console.error('Supabase Auth error:', data);
          if (data.error && (data.error.includes('already been registered') || data.error.includes('already exists'))) {
            if (data.userSynced) {
              alert(`A user with email ${newUser.email} already exists and has been synced to your portal. Please check the user list.`);
            } else {
              alert(`A user with email ${newUser.email} already exists in the authentication system. Please check the user list or use a different email address.`);
            }
            // Refresh the user list to make sure we show all users
            loadUsers();
          } else {
            alert(data.error || 'Failed to create user with Supabase Auth');
          }
        }
      } else {
        // Use legacy method - ensure password is provided
        if (!newUser.password_hash) {
          alert('Password is required for legacy authentication method');
          return;
        }

        const response = await fetch('https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/user-auth', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: user.email,
            action: 'create_user',
            newUser
          }),
        });

        const data = await response.json();
        
        if (response.ok) {
          setNewUser({
            email: '',
            password_hash: '',
            full_name: '',
            access_level: 'outsider'
          });
          loadUsers();
          alert('User created successfully!');
        } else {
          console.error('Legacy auth error:', data);
          if (data.error && data.error.includes('duplicate key')) {
            alert(`A user with email ${newUser.email} already exists. Please use a different email address.`);
          } else {
            alert(data.error || 'Failed to create user');
          }
        }
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Error creating user. Please check the console for details.');
    }
  };

  const loadUsers = async () => {
    try {
      const savedUser = localStorage.getItem('portalUser');
      if (!savedUser) return;
      
      const userData = JSON.parse(savedUser);
      if (userData.access_level !== 'master') return;

      // First try to get users from portal_users table
      const response = await fetch('https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/user-auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userData.email,
          action: 'get_users'
        }),
      });

      const data = await response.json();
      if (response.ok && data.users) {
        setUsers(data.users);
      }

      // Also try to sync with Supabase Auth users to catch any missing ones
      const syncResponse = await fetch('https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/auth-management', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userData.email,
          action: 'sync_auth_users'
        }),
      });

      if (syncResponse.ok) {
        // Reload users after sync
        const refreshResponse = await fetch('https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/user-auth', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: userData.email,
            action: 'get_users'
          }),
        });

        const refreshData = await refreshResponse.json();
        if (refreshResponse.ok && refreshData.users) {
          setUsers(refreshData.users);
        }
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadFolders = async () => {
    try {
      const savedUser = localStorage.getItem('portalUser');
      if (!savedUser) return;
      
      const userData = JSON.parse(savedUser);

      const response = await fetch('https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/folder-permissions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userData.email,
          action: 'get_user_folders'
        }),
      });

      const data = await response.json();
      if (response.ok && data.folders) {
        setFolders(data.folders);
      }
    } catch (error) {
      console.error('Error loading folders:', error);
    }
  };

  const deleteUser = async (userId: string) => {
    if (!user || user.access_level !== 'master') return;
    if (!confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch('https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/user-auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          action: 'delete_user',
          userId
        }),
      });

      const data = await response.json();
      if (response.ok) {
        loadUsers();
      } else {
        alert(data.error || 'Failed to delete user');
      }
    } catch (error) {
      alert('Error deleting user');
    }
  };

  const loadUserPermissions = async (targetUser: PortalUser) => {
    if (!user || user.access_level !== 'master') return;

    setSelectedUser(targetUser);
    setShowPermissions(true);

    try {
      const response = await fetch('https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/folder-permissions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          action: 'get_user_permissions',
          userId: targetUser.id
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setUserPermissions(data.folderIds || []);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  const saveUserPermissions = async () => {
    if (!user || user.access_level !== 'master' || !selectedUser) return;

    try {
      const response = await fetch('https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/folder-permissions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          action: 'set_user_permissions',
          userId: selectedUser.id,
          folderIds: userPermissions
        }),
      });

      const data = await response.json();
      if (response.ok) {
        alert('Permissions updated successfully');
        setShowPermissions(false);
      } else {
        alert(data.error || 'Failed to update permissions');
      }
    } catch (error) {
      alert('Error updating permissions');
    }
  };

  const toggleFolderPermission = (folderId: string) => {
    setUserPermissions(prev => 
      prev.includes(folderId) 
        ? prev.filter(id => id !== folderId)
        : [...prev, folderId]
    );
  };

  const getAccessLevelBadge = (level: string) => {
    const colors = {
      master: 'bg-red-100 text-red-800',
      editor: 'bg-blue-100 text-blue-800',
      partner: 'bg-green-100 text-green-800',
      outsider: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[level as keyof typeof colors]}`}>
        {level.charAt(0).toUpperCase() + level.slice(1)}
      </span>
    );
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-white" style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
        <Header />
        
        {/* Login Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-md mx-auto px-6">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-800 mb-2">Investor Portal</h1>
                <p className="text-gray-600 mb-4">Access your investment documents and reports</p>
                <Link 
                  to="/" 
                  className="inline-flex items-center text-sm text-[#9B1F3A] hover:text-[#7A1E2E] transition-colors"
                >
                  <i className="ri-arrow-left-line mr-1"></i>
                  Back to Home
                </Link>
              </div>
              
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B1F3A] focus:border-transparent"
                    placeholder="Enter your email"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B1F3A] focus:border-transparent"
                    placeholder="Enter your password"
                    required
                  />
                </div>
                
                {loginError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                    {loginError}
                  </div>
                )}
                
                <Button 
                  type="submit" 
                  className="w-full bg-[#9B1F3A] hover:bg-[#7A1E2E] text-white py-3 px-4 rounded-md font-medium transition-colors"
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setShowPasswordReset(true)}
                  className="text-sm text-[#9B1F3A] hover:text-[#7A1E2E] cursor-pointer"
                >
                  Forgot your password?
                </button>
              </div>
            </div>
          </div>
        </section>
        
        {/* Password Reset Modal */}
        {showPasswordReset && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Reset Password</h3>
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B1F3A]"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                  
                  {resetMessage && (
                    <div className={`px-4 py-3 rounded-md text-sm ${
                      resetMessage.includes('sent') 
                        ? 'bg-green-50 border border-green-200 text-green-700'
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                      {resetMessage}
                    </div>
                  )}
                  
                  <div className="flex justify-end space-x-2 mt-6">
                    <Button
                      type="button"
                      onClick={() => {
                        setShowPasswordReset(false);
                        setResetEmail('');
                        setResetMessage('');
                      }}
                      className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-[#9B1F3A] hover:bg-[#7A1E2E] text-white px-4 py-2 rounded-md"
                    >
                      Send Reset Email
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
      {/* Password Change Modal */}
      {showPasswordChange && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
              <p className="text-sm text-gray-600 mb-4">
                You are required to change your password before continuing.
              </p>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B1F3A]"
                    placeholder="Enter new password"
                    required
                    disabled={passwordLoading}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B1F3A]"
                    placeholder="Confirm new password"
                    required
                    disabled={passwordLoading}
                  />
                </div>
                
                {passwordError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                    {passwordError}
                  </div>
                )}
                
                <div className="flex justify-between space-x-2 mt-6">
                  <Button
                    type="button"
                    onClick={() => {
                      // Log out the user instead of bypassing password change
                      handleLogout();
                    }}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md"
                  >
                    Cancel & Logout
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#9B1F3A] hover:bg-[#7A1E2E] text-white px-4 py-2 rounded-md"
                    disabled={passwordLoading}
                  >
                    {passwordLoading ? 'Updating...' : 'Update Password'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center">
                <img 
                  src="https://images.canalrealty.us/Logos/logo-logotype.png" 
                  alt="Canal Realty Logo" 
                  className="h-8 sm:h-10 md:h-12 lg:h-14 w-auto object-contain flex-shrink-0"
                  style={{ minWidth: '140px', maxWidth: '200px' }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    console.log('Investor portal logo failed to load');
                    target.src = 'https://via.placeholder.com/189x50/9B1F3A/ffffff?text=CANAL+REALTY';
                  }}
                />
              </Link>
              {user && getAccessLevelBadge(user.access_level)}
            </div>
            <div className="flex-1 flex justify-center">
              <h1 className="text-2xl font-bold text-gray-900">Investor Portal</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Welcome, {user?.full_name}</span>
              {user?.access_level === 'master' && (
                <div className="flex space-x-2">
                  <Button
                    onClick={() => setShowUserManagement(!showUserManagement)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm"
                  >
                    <i className="ri-user-settings-line mr-2"></i>
                    Manage Users
                  </Button>
                </div>
              )}
              <Button
                onClick={handleLogout}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Master User Management */}
        {user?.access_level === 'master' && showUserManagement && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">User Management</h3>
                <Button
                  onClick={() => setShowUserPermissionsManager(!showUserPermissionsManager)}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md text-sm"
                >
                  <i className="ri-shield-user-line mr-2"></i>
                  Manage User Permissions
                </Button>
              </div>
            </div>
            <div className="p-6">
              {/* Create New User Form */}
              <div className="mb-8">
                <h4 className="text-md font-medium text-gray-900 mb-4">Create New User</h4>
                
                {/* Authentication Options */}
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <h5 className="text-sm font-medium text-gray-700 mb-3">Authentication Options</h5>
                  <div className="space-y-3">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={useSupabaseAuth}
                        onChange={(e) => setUseSupabaseAuth(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Use Supabase Authentication (recommended)</span>
                    </label>
                    
                    {useSupabaseAuth && (
                      <label className="flex items-center space-x-2 ml-6">
                        <input
                          type="checkbox"
                          checked={requirePasswordChange}
                          onChange={(e) => setRequirePasswordChange(e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Require password change on first login</span>
                      </label>
                    )}
                  </div>
                </div>

                <form onSubmit={createUser} className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <input
                    type="email"
                    placeholder="Email"
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    required
                  />
                  {!useSupabaseAuth && (
                    <input
                      type="password"
                      placeholder="Password"
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newUser.password_hash}
                      onChange={(e) => setNewUser({...newUser, password_hash: e.target.value})}
                      required
                    />
                  )}
                  <input
                    type="text"
                    placeholder="Full Name"
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                    required
                  />
                  <select
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newUser.access_level}
                    onChange={(e) => setNewUser({...newUser, access_level: e.target.value})}
                  >
                    <option value="outsider">Outsider</option>
                    <option value="partner">Partner</option>
                    <option value="editor">Editor</option>
                    <option value="master">Master</option>
                  </select>
                  <Button
                    type="submit"
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md whitespace-nowrap"
                  >
                    Create User
                  </Button>
                </form>
              </div>

              {/* Users List */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Existing Users</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Access Level</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((portalUser) => (
                        <tr key={portalUser.id}>
                          <td className="px-6 py-3 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{portalUser.full_name}</div>
                              <div className="text-sm text-gray-500">{portalUser.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap">
                            {getAccessLevelBadge(portalUser.access_level)}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              portalUser.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {portalUser.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm font-medium space-x-2">
                            {(portalUser.access_level === 'partner' || portalUser.access_level === 'outsider') && (
                              <button
                                onClick={() => loadUserPermissions(portalUser)}
                                className="text-blue-600 hover:text-blue-900 cursor-pointer"
                              >
                                <i className="ri-folder-settings-line mr-1"></i>
                                Permissions
                              </button>
                            )}
                            <button
                              onClick={() => deleteUser(portalUser.id)}
                              className="text-red-600 hover:text-red-900 cursor-pointer ml-4"
                            >
                              <i className="ri-delete-bin-line mr-1"></i>
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Permissions Manager */}
        {user?.access_level === 'master' && showUserPermissionsManager && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">User Folder Permissions</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Users List */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Select User</h4>
                  <div className="space-y-2 overflow-y-auto">
                    {users.filter(u => u.access_level === 'partner' || u.access_level === 'outsider').map((portalUser) => (
                      <button
                        key={portalUser.id}
                        onClick={() => loadUserPermissions(portalUser)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          selectedUser?.id === portalUser.id 
                            ? 'bg-blue-50 border-blue-300' 
                            : 'hover:bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">{portalUser.full_name}</div>
                            <div className="text-sm text-gray-500">{portalUser.email}</div>
                          </div>
                          {getAccessLevelBadge(portalUser.access_level)}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Folder Permissions */}
                {selectedUser && (
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-4">
                      Folder Access for {selectedUser.full_name}
                    </h4>
                    <div className="space-y-2 overflow-y-auto">
                      {folders.map((folder) => (
                        <label key={folder.id} className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={userPermissions.includes(folder.id)}
                            onChange={() => toggleFolderPermission(folder.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{folder.name}</div>
                            <div className="text-sm text-gray-500">Created: {new Date(folder.created_at).toLocaleDateString()}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                    <div className="mt-6">
                      <Button
                        onClick={saveUserPermissions}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md"
                      >
                        Save Permissions
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Folder Permissions Modal */}
        {showPermissions && selectedUser && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Folder Permissions for {selectedUser.full_name}
                </h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {folders.map((folder) => (
                    <label key={folder.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={userPermissions.includes(folder.id)}
                        onChange={() => toggleFolderPermission(folder.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-5"
                      />
                      <span className="text-sm text-gray-700">{folder.name}</span>
                    </label>
                  ))}
                </div>
                <div className="flex justify-end space-x-2 mt-6">
                  <Button
                    onClick={() => setShowPermissions(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={saveUserPermissions}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                  >
                    Save Permissions
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* File Upload Section - Only for Master and Editor */}
        {(user?.access_level === 'master' || user?.access_level === 'editor') && (
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Upload Documents</h3>
            </div>
            <div className="p-6">
              <FileUpload 
                onUploadComplete={() => setRefreshTrigger(prev => prev + 1)}
                userEmail={user.email}
                userAccessLevel={user.access_level}
              />
            </div>
          </div>
        )}

        {/* Integrated Folder Management and Document List */}
        {activeTab === 'folders' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <FolderManager 
                onFolderSelect={setSelectedFolderId}
                selectedFolderId={selectedFolderId}
                userEmail={user.email}
                userAccessLevel={user.access_level}
              />
            </div>
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {selectedFolderId ? 'Folder Documents' : 'All Documents'}
                </h3>
                <DocumentList 
                  refreshTrigger={refreshTrigger}
                  userAccessLevel={user.access_level}
                  userEmail={user.email}
                  selectedFolderId={selectedFolderId}
                />
              </div>
            </div>
          </div>
        )}

        {/* Document Repository (fallback if not using tabs) */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Document Repository</h3>
          </div>
          <div className="p-6">
            <DocumentList 
              refreshTrigger={refreshTrigger} 
              userAccessLevel={user.access_level}
              userEmail={user.email}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorPortal;
